import { useEffect, useRef, useState } from 'react'
import { Button } from '~/components/ui/Button'
import { DEFAULT_FB, type Chapter, type Regions } from '~/lib/adb/client'
import { sortedRegions, useChapter, useRegions } from '~/lib/adb/chapter'
import {
  farAwayMiles,
  useLocateNearestChapter,
} from '~/hooks/useLocateNearestChapter'
import { PageTitle } from '~/components/ui/PageTitle'

/**
 * Chapter selector button, "Find my chapter" geolocation, "View on Facebook"
 * and the full-screen chapter picker. Selection is shared via useChapter.
 */
export function ChapterBar() {
  const { chapter, select } = useChapter()
  const [picking, setPicking] = useState(false)
  const [farAway, setFarAway] = useState<number | null>(null)
  const { state: locating, locate, reset } = useLocateNearestChapter()
  // Fetched when the picker first opens, unless a page already put them in
  // the query cache (`useRegions(seed)` on /events, or a loader).
  const regions = useRegions(undefined, picking)

  async function findMyChapter() {
    const nearest = await locate()
    if (!nearest) return
    select(nearest)
    setFarAway(farAwayMiles(nearest.Distance))
  }

  return (
    <div className="flex flex-col gap-6 pb-6">
      <div className="flex items-center max-lg:flex-col max-lg:items-start lg:flex-wrap">
        <button
          type="button"
          onClick={() => setPicking(true)}
          className="flex w-full cursor-pointer items-center justify-between border-0 border-b-[3px] border-fg bg-transparent transition-colors hover:border-royal py-2.5 pl-0.5 text-left text-lg leading-[26px] text-fg-body max-[479px]:text-base lg:ml-[50px] lg:max-w-[500px] lg:min-w-[260px] lg:flex-1"
        >
          <span>{chapter.Name}</span>
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <path
              d="M2 5l5 5 5-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
        </button>
        <div className="flex max-md:w-full max-md:flex-col lg:shrink-0">
          <Button
            onClick={findMyChapter}
            disabled={locating.status === 'locating'}
            className="mt-[15px] min-w-max font-medium lg:ml-[30px]"
          >
            {locating.status === 'locating' ? 'Locating...' : 'Find my chapter'}
          </Button>
          <Button
            href={`${chapter.FbURL || DEFAULT_FB}/events`}
            target="_blank"
            rel="noreferrer"
            variant="outline"
            className="mt-[15px] min-w-max lg:ml-10"
          >
            View {chapter.Name} on Facebook
          </Button>
        </div>
      </div>

      {locating.status === 'error' && (
        <p role="alert" className="m-0 text-fg-muted">
          {locating.message}
        </p>
      )}

      {farAway !== null && (
        <div>
          <h3 className="mb-1 text-xl font-bold text-fg">
            The closest chapter to you is {farAway} miles away.
          </h3>
          <p className="m-0">
            Fill out{' '}
            <a
              href="https://adb.dxe.io/international"
              target="_blank"
              rel="noreferrer"
              className="font-bold text-accent hover:underline"
            >
              this form
            </a>{' '}
            to start a chapter of your own!
          </p>
        </div>
      )}

      {picking && (
        <ChapterPicker
          regions={regions}
          onSelect={(c) => {
            select(c)
            setFarAway(null)
            reset()
          }}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  )
}

/**
 * Full-screen modal list of every chapter, on a native <dialog>: showModal()
 * gives the focus containment, inert page behind, Escape, top layer and focus
 * restore to the trigger. `onSelect` picks a chapter; the picker then closes
 * itself, and `onClose` fires (once) when it has.
 */
function ChapterPicker({
  regions,
  onSelect,
  onClose,
}: {
  regions: Regions
  onSelect: (c: Chapter) => void
  onClose: () => void
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  const [closing, setClosing] = useState(false)

  useEffect(() => {
    const d = dialog.current
    if (d && !d.open) d.showModal()
  }, [])

  // Let the fade-out finish (none under reduced motion) before closing: close()
  // restores focus to the trigger and fires `close`, which unmounts us. A
  // second Escape while fading just lands here again as a no-op.
  useEffect(() => {
    const d = dialog.current
    if (!closing || !d) return
    let cancelled = false
    void Promise.allSettled(d.getAnimations().map((a) => a.finished)).then(
      () => {
        if (!cancelled) d.close()
      },
    )
    return () => {
      cancelled = true
    }
  }, [closing])

  return (
    <dialog
      ref={dialog}
      aria-label="Select your location"
      // Escape: fade out instead of the browser's instant close.
      onCancel={(e) => {
        e.preventDefault()
        setClosing(true)
      }}
      onClose={onClose}
      // The UA gives a modal dialog a centred, shrink-to-fit box; this is a
      // full-screen sheet that scrolls itself. `starting:` fades it in.
      className={`m-0 size-full max-h-none max-w-none overflow-y-scroll overscroll-contain border-0 bg-surface-alt/[0.97] p-0 pt-[75px] pb-10 text-fg-body transition-opacity duration-200 backdrop:bg-transparent motion-reduce:transition-none starting:open:opacity-0 ${
        closing ? 'opacity-0' : 'opacity-100'
      }`}
    >
      <div className="relative mx-auto w-[90%] max-w-site">
        <PageTitle as="h2" size="sm" className="mb-10">
          Select your location
        </PageTitle>
        <button
          type="button"
          aria-label="Close"
          onClick={() => setClosing(true)}
          className="fixed top-[2%] right-[6%] cursor-pointer rounded-full transition-opacity hover:opacity-70 border-0 bg-transparent p-0 lg:absolute lg:top-[-30px] lg:right-0"
        >
          <img src="/images/close-icon.svg" alt="" width={64} height={64} />
        </button>
        {sortedRegions(regions, true).map(({ region, chapters }) => (
          <section key={region} className="mb-[30px] md:mb-[50px]">
            <h3 className="mt-0 mb-[15px] text-base leading-6 font-semibold tracking-normal text-fg-body uppercase md:mb-[33px]">
              {region}
            </h3>
            <div className="grid grid-cols-1 gap-y-[15px] md:grid-cols-5 md:gap-x-[5px] md:gap-y-[35px]">
              {chapters.map((c) => (
                <button
                  key={c.Name}
                  type="button"
                  onClick={() => {
                    onSelect(c)
                    setClosing(true)
                  }}
                  className="cursor-pointer border-0 bg-transparent p-0 text-left text-lg leading-[26px] font-normal text-accent hover:underline"
                >
                  {c.Flag} {c.Name}
                </button>
              ))}
            </div>
          </section>
        ))}
        <div className="mt-[60px] mb-[120px] flex flex-col items-center gap-3 text-center">
          <p className="m-0 text-xl font-bold">Don't see anything near you?</p>
          <Button href="/new-chapter">Start a chapter in your city</Button>
          <p className="m-0">
            Contact{' '}
            <a
              href="mailto:mentoring@dxe.io"
              className="font-bold text-accent hover:underline"
            >
              mentoring@dxe.io
            </a>{' '}
            to find other ways to get involved.
          </p>
        </div>
      </div>
    </dialog>
  )
}
